import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  resetCollection,
  resetCompetition,
  resetTask,
  setCountryOptions,
  setLanguageOptions,
  setRoleOptions,
  userLogout,
} from "../actions";
import { isTeacher } from "../functions/checkrole";
import { logout } from "../functions/httpMethods";
import { AppBar } from "./DrawerAndAppBar";

const roles = [
  { id: 0, name: "Super Admin" },
  { id: 1, name: "Admin" },
  { id: 2, name: "Country Partner" },
  { id: 4, name: "Country Partner Assistant" },
  { id: 5, name: "School Manager" },
  { id: 3, name: "Teacher" },
];

const routeLabels = {
  "/dashboard": "Dashboard",
  "/competitions": "Competitions",
  "/teamcompetitions": "Team Competitions",
  "/task": "Tasks",
  "/addtask": "Add Task",
  "/edittask": "Edit Task",
  "/collection": "Collections",
  "/addcollection": "Add Collection",
  "/editcollection": "Edit Collection",
  "/assigntranslation": "Assign Translation",
  "/translate": "Translate",
  "/downloadcontestpaper": "Download Contest Paper",
  "/domain": "Domains & Tags",
  "/difficultygroup": "Difficulty Groups",
  "/participants": "Participants",
  "/uploadedparticipantsfiles": "Participant Imports",
  "/certificatetemplates": "Certificate Templates",
  "/configurecertificate": "Configure Certificate",
  "/printcertificate": "Print Certificates",
  "/users": "Users",
  "/organizations": "Organizations",
  "/schools": "Schools",
  "/profile": "Profile",
};

const getRouteLabel = (pathname) => {
  if (routeLabels[pathname]) return routeLabels[pathname];

  const segment = pathname.split("/").filter(Boolean)[0];
  if (!segment) return "Dashboard";
  return segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";

export default function NavBar({ open, toggleDrawer, mobile }) {
  const [anchorElNotif, setAnchorElNotif] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const roleName =
    roles.find((role) => role.id === Number(user.role_id))?.name || "User";
  const pageTitle = getRouteLabel(location.pathname);

  const toggleNotification = (event) => {
    setAnchorElNotif(anchorElNotif ? null : event.currentTarget);
    setAnchorElUser(null);
  };

  const toggleUser = (event) => {
    setAnchorElUser(anchorElUser ? null : event.currentTarget);
    setAnchorElNotif(null);
  };

  const logoutUser = () => {
    logout().then(() => {
      dispatch(resetTask());
      dispatch(resetCollection());
      dispatch(resetCompetition());
      dispatch(userLogout());
      dispatch(setLanguageOptions([]));
      dispatch(setCountryOptions([]));
      dispatch(setRoleOptions([]));
    });
    setAnchorElUser(null);
  };

  return (
    <AppBar mobile={mobile}>
      <Box className="simcc-topbar__leading">
        {(mobile || !open) && (
          <IconButton
            onClick={() => toggleDrawer(true)}
            className="simcc-topbar__icon-button"
            aria-label="Open navigation"
            size="small"
          >
            <MenuIcon />
          </IconButton>
        )}
        <div className="simcc-topbar__breadcrumb" aria-label="Breadcrumb">
          <span>SIMCC</span>
          <ChevronRightIcon aria-hidden="true" />
          <strong>{pageTitle}</strong>
        </div>
      </Box>

      <Box className="simcc-topbar__actions">
        <IconButton
          className="simcc-topbar__icon-button simcc-topbar__help"
          aria-label="Help"
          title="Help"
          size="small"
        >
          <HelpOutlineIcon />
        </IconButton>
        <IconButton
          onClick={toggleNotification}
          className="simcc-topbar__icon-button simcc-topbar__notification"
          aria-label="Notifications"
          aria-expanded={Boolean(anchorElNotif)}
          size="small"
        >
          <NotificationsNoneIcon />
          <span className="simcc-topbar__notification-dot" />
        </IconButton>
        <ButtonBase
          onClick={toggleUser}
          className="simcc-topbar__user-pill"
          aria-label="Open user menu"
          aria-expanded={Boolean(anchorElUser)}
        >
          <Avatar className="simcc-topbar__avatar">
            {getInitials(user.username)}
          </Avatar>
          <span className="simcc-topbar__user-copy">
            <strong>{user.username || "User"}</strong>
            <small>{roleName}</small>
          </span>
          <KeyboardArrowDownIcon className="simcc-topbar__user-chevron" />
        </ButtonBase>

        {Boolean(anchorElNotif) && (
          <ClickAwayListener onClickAway={() => setAnchorElNotif(null)}>
            <Popper
              open
              anchorEl={anchorElNotif}
              placement="bottom-end"
              className="simcc-topbar__popper"
            >
              <Paper className="simcc-topbar__menu simcc-topbar__notifications-menu">
                <div className="simcc-topbar__menu-heading">
                  <strong>Notifications</strong>
                  <span>3 new</span>
                </div>
                {[
                  "Test1",
                  "Test2",
                  "Test3",
                ].map((notification, index) => (
                  <div className="simcc-topbar__notification-row" key={index}>
                    <span className="simcc-topbar__notification-mark" />
                    <div>
                      <strong>Notification</strong>
                      <p>{notification}</p>
                    </div>
                  </div>
                ))}
                <Button fullWidth className="simcc-topbar__view-all">
                  View all notifications
                </Button>
              </Paper>
            </Popper>
          </ClickAwayListener>
        )}

        {Boolean(anchorElUser) && (
          <ClickAwayListener onClickAway={() => setAnchorElUser(null)}>
            <Popper
              open
              anchorEl={anchorElUser}
              placement="bottom-end"
              className="simcc-topbar__popper"
            >
              <Paper className="simcc-topbar__menu simcc-topbar__user-menu">
                <div className="simcc-topbar__profile-summary">
                  <Avatar>{getInitials(user.username)}</Avatar>
                  <div>
                    <Typography component="strong">
                      {user.username || "User"}
                    </Typography>
                    <Typography component="span">{roleName}</Typography>
                  </div>
                </div>
                <div className="simcc-topbar__menu-links">
                  <Button
                    component={Link}
                    to={isTeacher() ? "/participants" : "/schools"}
                    onClick={() => setAnchorElUser(null)}
                    startIcon={<HomeOutlinedIcon />}
                  >
                    Home
                  </Button>
                  <Button
                    component={Link}
                    to="/profile"
                    onClick={() => setAnchorElUser(null)}
                    startIcon={<PersonOutlineIcon />}
                  >
                    Profile
                  </Button>
                </div>
                <Button
                  onClick={logoutUser}
                  startIcon={<LogoutIcon />}
                  className="simcc-topbar__logout"
                  fullWidth
                >
                  Log Out
                </Button>
              </Paper>
            </Popper>
          </ClickAwayListener>
        )}
      </Box>
    </AppBar>
  );
}

NavBar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  mobile: PropTypes.bool.isRequired,
};
